import {
  KeyForagesV1,
  SeasonalBaitColorsV1,
  SeasonalPatternsV2,
  SeasonalStructuresV1,
  SeasonNames,
  WaterbodyDetail,
} from '@omniafishing/core';
import { Collapse } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import { ContentWrapper } from '../../components/content_wrapper/content_wrapper';
import {
  TableKeyForage,
  TableSeasonalBaitColors,
  TableSeasonalPatterns,
  TableSeasonalStructures,
} from '../../components/map/map_modals/lakes_modal/lake_summary';
import { ScrollToTop } from '../../components/scroll_to_top/scroll_to_top';
import { SEO } from '../../components/seo/seo';
import { FIRST_PUBLISHED_DATE } from '../../components/waterbody_detail/waterbody_detail';
import { WaterbodyDetailHeader } from '../../components/waterbody_detail/waterbody_detail_sections/waterbody_detail_header/waterbody_detail_header';
import { waterbodyImgUrl } from '../../components/waterbody_img/waterbody_img';
import { useReferenceData } from '../../hooks/use_reference_data';
import { useUser } from '../../hooks/use_user';
import styles from './waterbody_fishing_patterns_page.less';

dayjs.extend(utc);

export enum ConditionsPanelKeys {
  CurrentWaterTemperature = '1',
  UpdateFrequency = '2',
}

const springNames: SeasonNames[] = ['post_spawn', 'pre_spawn', 'spawn'];

interface WaterbodyFishingPattnersPageProps {
  waterbodyDetail: WaterbodyDetail;
  summary: {
    key_forage: KeyForagesV1 | null;
    seasonal_bait_colors: SeasonalBaitColorsV1 | null;
    seasonal_patterns: SeasonalPatternsV2 | null;
    seasonal_structure: SeasonalStructuresV1 | null;
  };
  lastUpdatedISODate: string;
}

export const WaterbodyFishingPatternsPage = (props: WaterbodyFishingPattnersPageProps) => {
  const { waterbodyDetail, summary, lastUpdatedISODate } = props;

  const { water_temperature_recorded_at, primary_name } = waterbodyDetail;
  const { isPro } = useUser();
  const { allSeasons } = useReferenceData();
  const spring = allSeasons.filter((s) => springNames.includes(s.name));
  const summer = allSeasons.find((s) => s.name === 'summer');
  const fall = allSeasons.find((s) => s.name === 'fall');
  const winterIce = allSeasons.filter((s) => s.name === 'ice' || s.name === 'winter');

  const springPatterns = summary.seasonal_patterns.patterns.filter((sp) => {
    const springDisplayNames = spring.map((s) => s.display_name);
    return springDisplayNames.includes(sp.season);
  });
  const summerPatterns = summary.seasonal_patterns.patterns.filter(
    (sp) => sp.season === summer.display_name
  );
  const fallPatterns = summary.seasonal_patterns.patterns.filter(
    (sp) => sp.season === fall.display_name
  );
  const winterIcePatterns = summary.seasonal_patterns.patterns.filter((sp) => {
    const winterIceDisplayNames = winterIce.map((s) => s.display_name);
    return winterIceDisplayNames.includes(sp.season);
  });

  const schema: Record<string, string | string[]> = {
    '@context': 'https://schema.org/',
    '@type': 'Article',
    datePublished: FIRST_PUBLISHED_DATE,
    dateModified: dayjs(lastUpdatedISODate).format('YYYY-MM-DD'),
    description: `Unlock the best fishing patterns for ${primary_name}! Find top spots, seasonal techniques, and the best lures for bass, walleye, crappie, and more.`,
    headline: `How to Fish ${primary_name}: Best Patterns & Techniques`,
    image: [
      waterbodyImgUrl(waterbodyDetail, { imgSize: { width: 800, height: 600 }, hires: false }),
    ],
  };

  return (
    <ScrollToTop>
      <SEO
        title={`How to Fish ${waterbodyDetail.primary_name}: Best Patterns & Techniques`}
        description={`Unlock the best fishing patterns for ${waterbodyDetail.primary_name}! Find top spots, seasonal techniques, and the best lures for bass, walleye, crappie, and more.`}
        meta={[{ name: 'date', content: lastUpdatedISODate }]}
      />
      <section>
        <WaterbodyDetailHeader
          headingTag="p"
          productToShowInFollowModal={null}
          scrollToFishingReport={() => null}
          waterbody={waterbodyDetail}
          lastUpdatedDate={water_temperature_recorded_at}
          showPublishDates
          navElement={null}
          showBreadcrumbs={false}
          showButtons={false}
        />
        <section>
          <ContentWrapper>
            <div className="py-6 relative">
              <section
                className={classNames(
                  styles.toc,
                  'sticky top-[76px] sm:top-0 z-10 bg-white border-b mb-12'
                )}
              >
                <Collapse ghost expandIconPosition="end">
                  <Collapse.Panel header="Table of Contents" key="1">
                    <nav>
                      <ul>
                        {springPatterns.length > 0 && (
                          <li>
                            <a className="inline-block px-1 py-0.5" href="#spring-fishing-patterns">
                              Spring Fishing Patterns
                            </a>
                          </li>
                        )}
                        {summerPatterns.length > 0 && (
                          <li>
                            <a className="inline-block px-1 py-0.5" href="#summer-fishing-patterns">
                              Summer Fishing Patterns
                            </a>
                          </li>
                        )}
                        {fallPatterns.length > 0 && (
                          <li>
                            <a className="inline-block px-1 py-0.5" href="#fall-fishing-guide">
                              Fall Fishing Guide
                            </a>
                          </li>
                        )}
                        {winterIcePatterns.length > 0 && (
                          <li>
                            <a
                              className="inline-block px-1 py-0.5"
                              href="#winter-ice-fishing-patterns"
                            >
                              Winter & Ice Fishing Patterns
                            </a>
                          </li>
                        )}
                        <li>
                          <a className="inline-block px-1 py-0.5" href="#key-seasonal-structures">
                            Key Seasonal Structures
                          </a>
                        </li>
                        <li>
                          <a className="inline-block px-1 py-0.5" href="#primary-forage">
                            Primary Forage by Season
                          </a>
                        </li>
                        <li>
                          <a className="inline-block px-1 py-0.5" href="#bait-colors-by-season">
                            Bait Colors by Season
                          </a>
                        </li>
                        <li>
                          <a className="inline-block px-1 py-0.5" href="#bait-colors-by-species">
                            Bait Colors by Species
                          </a>
                        </li>
                        <li>
                          <a className="inline-block px-1 py-0.5" href="#bait-colors-by-technique">
                            Best Bait Colors by Technique
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </Collapse.Panel>
                </Collapse>
              </section>

              {springPatterns.length > 0 && (
                <>
                  <h2 id="spring-fishing-patterns" className="text-lg mb-3">
                    Spring Fishing Patterns on {primary_name}
                  </h2>
                  <div className="overflow-auto mb-12">
                    <TableSeasonalPatterns patterns={springPatterns} />
                  </div>
                </>
              )}

              {summerPatterns.length > 0 && (
                <>
                  <h2 id="summer-fishing-patterns" className="text-lg mb-3">
                    Summer Fishing Patterns
                  </h2>
                  <div className="overflow-auto mb-12">
                    <TableSeasonalPatterns patterns={summerPatterns} />
                  </div>
                </>
              )}

              {fallPatterns.length > 0 && (
                <>
                  <h2 id="fall-fishing-guide" className="text-lg mb-3">
                    Fall Fishing Guide for {primary_name}
                  </h2>
                  <div className="overflow-auto mb-12">
                    <TableSeasonalPatterns patterns={fallPatterns} />
                  </div>
                </>
              )}

              {winterIcePatterns.length > 0 && (
                <>
                  <h2 id="winter-ice-fishing-patterns" className="text-lg mb-3">
                    Winter & Ice Fishing Patterns
                  </h2>
                  <div className="overflow-auto mb-12">
                    <TableSeasonalPatterns patterns={winterIcePatterns} />
                  </div>
                </>
              )}

              <h2 id="key-seasonal-structures" className="text-lg mb-3">
                Key Seasonal Structures in {primary_name}
              </h2>
              <div className="overflow-auto mb-12">
                <TableSeasonalStructures seasonalStructure={summary.seasonal_structure} />
              </div>

              <h2 id="primary-forage" className="text-lg mb-3">
                Primary Forage by Season
              </h2>
              <div className="overflow-auto mb-12">
                <TableKeyForage keyForage={summary.key_forage} frosted={!isPro} />
              </div>

              <h2 id="bait-colors-by-season" className="text-lg mb-3">
                Bait Colors by Season for {primary_name}
              </h2>
              <div className="overflow-auto mb-12">
                <TableSeasonalBaitColors
                  baitColorsBySeason={summary.seasonal_bait_colors}
                  frosted={!isPro}
                />
              </div>
            </div>
          </ContentWrapper>
        </section>
      </section>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />
    </ScrollToTop>
  );
};
